import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Globe, Link2, Check, X, ChevronRight, Zap, AlertCircle,
  RefreshCw, ExternalLink, Shield, Settings
} from 'lucide-react';
import { toast } from 'sonner';

const INTEGRATIONS = [
  {
    category: 'Google',
    items: [
      { id: 'google_analytics', name: 'Google Analytics', desc: 'Website verkeer en conversies bijhouden', icon: '📊', color: '#4285F4' },
      { id: 'google_ads', name: 'Google Ads', desc: 'Advertentie campagnes beheren', icon: '📢', color: '#34A853' },
      { id: 'google_business', name: 'Google Bedrijfsprofiel', desc: 'Google Maps en lokale zichtbaarheid', icon: '📍', color: '#FBBC05' },
      { id: 'google_search_console', name: 'Search Console', desc: 'Zoekprestaties en indexering monitoren', icon: '🔍', color: '#EA4335' },
    ]
  },
  {
    category: 'Social Media',
    items: [
      { id: 'meta_facebook', name: 'Facebook / Meta', desc: 'Facebook pagina en advertenties beheren', icon: '📘', color: '#1877F2' },
      { id: 'meta_instagram', name: 'Instagram', desc: 'Instagram bedrijfsaccount koppelen', icon: '📸', color: '#E4405F' },
      { id: 'tiktok', name: 'TikTok', desc: 'TikTok bedrijfsaccount en advertenties', icon: '🎵', color: '#000000' },
      { id: 'linkedin', name: 'LinkedIn', desc: 'LinkedIn bedrijfspagina koppelen', icon: '💼', color: '#0077B5' },
    ]
  },
  {
    category: 'Website Builders (API)',
    desc: 'Automatische wijzigingen via REST API — onze AI past H1/meta/content rechtstreeks aan',
    items: [
      { id: 'wordpress', name: 'WordPress', desc: 'Automatisch SEO fixes doorvoeren via REST API', icon: '🔧', color: '#21759B', hasConfig: true, mode: 'auto' },
      { id: 'shopify', name: 'Shopify', desc: 'Shopify webshop koppelen via Admin API', icon: '🛒', color: '#96BF48', mode: 'auto' },
      { id: 'webflow', name: 'Webflow', desc: 'Webflow CMS via API koppelen', icon: '◢', color: '#4353FF', mode: 'auto' },
      { id: 'magento', name: 'Magento', desc: 'Magento webshop via REST API', icon: '🛍️', color: '#EE672F', mode: 'auto' },
      { id: 'prestashop', name: 'PrestaShop', desc: 'PrestaShop webshop via API', icon: '🛒', color: '#DF0067', mode: 'auto' },
      { id: 'bigcommerce', name: 'BigCommerce', desc: 'BigCommerce store via API', icon: '🏪', color: '#34313F', mode: 'auto' },
    ]
  },
  {
    category: 'Website Builders (handmatig)',
    desc: 'Geen publieke write-API — onze AI levert kant-en-klare snippets die u zelf plakt, of ons team doet de plaatsing handmatig',
    items: [
      { id: 'wix', name: 'Wix', desc: 'Snippets klaar om in Wix editor te plakken', icon: '🌐', color: '#0C6EFC', mode: 'manual' },
      { id: 'squarespace', name: 'Squarespace', desc: 'AI snippets voor Squarespace blocks', icon: '⬛', color: '#000000', mode: 'manual' },
      { id: 'webador', name: 'Webador (NL)', desc: 'Nederlandse builder — handmatige snippets', icon: '🇳🇱', color: '#FF6B35', mode: 'manual' },
      { id: 'jouwweb', name: 'JouwWeb (NL)', desc: 'Nederlandse builder — handmatige snippets', icon: '🇳🇱', color: '#00A86B', mode: 'manual' },
      { id: 'strikingly', name: 'Strikingly', desc: 'One-page builder — handmatige snippets', icon: '⚡', color: '#1B5E20', mode: 'manual' },
      { id: 'webnode', name: 'Webnode', desc: 'Builder — handmatige plaatsing door ons team', icon: '🌍', color: '#0EA5E9', mode: 'manual' },
      { id: 'site123', name: 'Site123', desc: 'Snel-builder — handmatige snippets', icon: '🔢', color: '#FFB400', mode: 'manual' },
      { id: 'mozello', name: 'Mozello', desc: 'EU builder — handmatige plaatsing', icon: '🌿', color: '#4CAF50', mode: 'manual' },
      { id: 'ucraft', name: 'Ucraft', desc: 'Builder — handmatige snippets', icon: '🎨', color: '#5D5FEF', mode: 'manual' },
      { id: 'google_sites', name: 'Google Sites', desc: 'Beperkte SEO — handmatige optimalisatie', icon: '📄', color: '#4285F4', mode: 'manual' },
    ]
  },
  {
    category: 'CMS / Hosting',
    desc: 'Open-source CMS via FTP / database — vereist credentials',
    items: [
      { id: 'joomla', name: 'Joomla', desc: 'Joomla CMS via FTP/API', icon: '🔵', color: '#5091CD', mode: 'manual' },
      { id: 'drupal', name: 'Drupal', desc: 'Drupal CMS via REST API', icon: '💧', color: '#0678BE', mode: 'manual' },
      { id: 'typo3', name: 'TYPO3', desc: 'TYPO3 enterprise CMS', icon: '🟠', color: '#FF8700', mode: 'manual' },
      { id: 'opencart', name: 'OpenCart', desc: 'OpenCart webshop', icon: '🛒', color: '#2AC2EF', mode: 'manual' },
      { id: 'mijndomein', name: 'Mijndomein (NL)', desc: 'Nederlandse hosting — handmatige acties', icon: '🇳🇱', color: '#FF5C00', mode: 'manual' },
      { id: 'hostnet', name: 'Hostnet (NL)', desc: 'NL hosting — handmatige plaatsing', icon: '🇳🇱', color: '#003E7E', mode: 'manual' },
      { id: 'combell', name: 'Combell', desc: 'Belgisch/NL hosting', icon: '🇧🇪', color: '#E60012', mode: 'manual' },
      { id: 'versio', name: 'Versio', desc: 'NL hosting', icon: '🇳🇱', color: '#0072CE', mode: 'manual' },
    ]
  },
  {
    category: 'Overig',
    items: [
      { id: 'mailchimp', name: 'Mailchimp', desc: 'Email marketing automatisering', icon: '📧', color: '#FFE01B' },
      { id: 'hotjar', name: 'Hotjar', desc: 'Heatmaps en gebruikersgedrag', icon: '🔥', color: '#FF3C00' },
    ]
  },
];

function ConnectModal({ integration, onClose, onConnect }) {
  const [config, setConfig] = useState({});
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const handleConnect = async () => {
    setError('');
    setConnecting(true);
    const result = await onConnect(integration.id, config);
    if (!result) setConnecting(false);
  };

  const isWebsiteBuilder = ['wordpress', 'wix', 'squarespace', 'shopify'].includes(integration.id);
  const isOverig = ['mailchimp', 'hotjar'].includes(integration.id);

  const PROVIDER_FIELDS = {
    google_analytics: [
      { key: 'property_id', label: 'Property ID', placeholder: 'Bijv. 123456789', help: 'Ga naar Google Analytics > Beheer > Property-instellingen > Property-ID' },
    ],
    google_ads: [
      { key: 'customer_id', label: 'Klant-ID', placeholder: 'Bijv. 123-456-7890', help: 'Ga naar Google Ads > Instellingen > Accountinstellingen > Klant-ID' },
    ],
    google_business: [
      { key: 'location_id', label: 'Locatie-ID', placeholder: 'Bijv. locations/12345678', help: 'Ga naar Google Bedrijfsprofiel > Info > Bekijk uw profiel-ID' },
    ],
    google_search_console: [
      { key: 'site_url', label: 'Geverifieerde Site URL', placeholder: 'https://uwwebsite.nl', help: 'De exact URL zoals geverifieerd in Search Console' },
    ],
    meta_facebook: [
      { key: 'page_id', label: 'Pagina-ID', placeholder: 'Bijv. 123456789012345', help: 'Ga naar uw Facebook Pagina > Info > Pagina-ID' },
      { key: 'access_token', label: 'Access Token', placeholder: 'Uw Facebook Graph API token', help: 'Genereer via developers.facebook.com > Graph API Explorer', isSecret: true },
    ],
    meta_instagram: [
      { key: 'account_id', label: 'Bedrijfsaccount ID', placeholder: 'Bijv. 17841400123456', help: 'Verkrijg via Facebook Graph API of Business Suite' },
      { key: 'access_token', label: 'Access Token', placeholder: 'Uw Instagram API token', help: 'Genereer via developers.facebook.com', isSecret: true },
    ],
    tiktok: [
      { key: 'advertiser_id', label: 'Advertiser ID', placeholder: 'Bijv. 7123456789012', help: 'Ga naar TikTok Ads Manager > Account Info' },
      { key: 'access_token', label: 'Access Token', placeholder: 'Uw TikTok API token', help: 'Genereer via TikTok for Business developers portal', isSecret: true },
    ],
    linkedin: [
      { key: 'organization_id', label: 'Organisatie ID', placeholder: 'Bijv. 12345678', help: 'Ga naar LinkedIn bedrijfspagina > Admin tools > pagina-ID' },
      { key: 'access_token', label: 'Access Token', placeholder: 'Uw LinkedIn API token', help: 'Genereer via LinkedIn Developer Portal', isSecret: true },
    ],
  };

  const fields = PROVIDER_FIELDS[integration.id] || [];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
      <div data-testid="connect-modal" style={{ position: 'relative', background: 'white', borderRadius: '18px', maxWidth: '520px', width: '100%', padding: '28px', boxShadow: '0 24px 48px rgba(0,0,0,0.15)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${integration.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>
            {integration.icon}
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
              {integration.name} koppelen
            </h3>
            <p style={{ fontSize: '12px', color: '#94a3b8' }}>{integration.desc}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px' }}>
            <X size={18} />
          </button>
        </div>

        {/* OAuth / API fields */}
        {fields.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '20px' }}>
            {fields.map(f => (
              <div key={f.key}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                  {f.label} <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type={f.isSecret ? 'password' : 'text'}
                  value={config[f.key] || ''}
                  onChange={e => setConfig(c => ({ ...c, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  data-testid={`connect-${f.key}`}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
                {f.help && <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>{f.help}</p>}
              </div>
            ))}
          </div>
        )}

        {/* Website builders */}
        {isWebsiteBuilder && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                Website URL <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="text" value={config.url || ''}
                onChange={e => setConfig(c => ({ ...c, url: e.target.value }))}
                placeholder="https://www.uwwebsite.nl"
                data-testid="connect-url-input"
                style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            {integration.id === 'wordpress' && (
              <>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Gebruikersnaam <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input type="text" value={config.username || ''} onChange={e => setConfig(c => ({ ...c, username: e.target.value }))} placeholder="admin"
                    data-testid="connect-username-input"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                    Application Password <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input type="password" value={config.api_key || ''} onChange={e => setConfig(c => ({ ...c, api_key: e.target.value }))} placeholder="xxxx xxxx xxxx xxxx"
                    data-testid="connect-apikey-input"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div style={{ background: '#f0f9ff', borderRadius: '10px', padding: '12px', border: '1px solid #bae6fd' }}>
                  <p style={{ fontSize: '11px', color: '#0369a1', lineHeight: 1.6 }}>
                    <strong>Hoe maak ik een Application Password?</strong><br />
                    1. Ga naar WordPress Dashboard {'>'} Gebruikers {'>'} Profiel<br />
                    2. Scroll naar "Application Passwords"<br />
                    3. Voer "IMPERIA AI Radar" in als naam en klik op "Toevoegen"<br />
                    4. Kopieer het wachtwoord en plak het hierboven
                  </p>
                </div>
              </>
            )}
            {integration.id !== 'wordpress' && (
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                  API Key <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input type="password" value={config.api_key || ''} onChange={e => setConfig(c => ({ ...c, api_key: e.target.value }))} placeholder="Uw API sleutel"
                  data-testid="connect-apikey-input"
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            )}
          </div>
        )}

        {/* Overig (mailchimp, hotjar) */}
        {isOverig && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
              API Key <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input type="password" value={config.api_key || ''} onChange={e => setConfig(c => ({ ...c, api_key: e.target.value }))} placeholder="Uw API sleutel"
              data-testid="connect-apikey-input"
              style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px', padding: '10px', background: 'rgba(212,160,23,0.05)', borderRadius: '8px' }}>
          <Zap size={14} style={{ color: '#D4A017', flexShrink: 0 }} />
          <p style={{ fontSize: '11px', color: '#64748b' }}>
            Na koppeling kan IMPERIA AI automatisch verbeteringen doorvoeren en live data tonen in uw dashboard.
          </p>
        </div>

        {error && (
          <div style={{ padding: '10px', background: '#fef2f2', borderRadius: '8px', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AlertCircle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
            <p style={{ fontSize: '12px', color: '#dc2626' }}>{error}</p>
          </div>
        )}

        <button
          data-testid="confirm-connect-btn"
          onClick={handleConnect}
          disabled={connecting}
          style={{
            width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
            background: '#0f172a', color: 'white', fontSize: '14px', fontWeight: 700,
            cursor: connecting ? 'wait' : 'pointer', opacity: connecting ? 0.7 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          }}
        >
          {connecting ? <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <Link2 size={16} />}
          {connecting ? 'Verbinden...' : `${integration.name} koppelen`}
        </button>
      </div>
    </div>
  );
}

export default function IntegratiePage() {
  const { api } = useAuth();
  const [connected, setConnected] = useState({});
  const [loading, setLoading] = useState(true);
  const [connectingItem, setConnectingItem] = useState(null);
  const [oauthProviders, setOauthProviders] = useState({});

  useEffect(() => {
    // Check for OAuth callback success
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'success') {
      const provider = params.get('provider');
      toast.success(`${provider} succesvol gekoppeld via OAuth!`);
      window.history.replaceState({}, '', window.location.pathname);
    }

    Promise.all([
      api('get', '/api/platform/integrations'),
      api('get', '/api/integrations/oauth/providers').catch(() => ({ data: { providers: [] } })),
    ]).then(([intRes, oauthRes]) => {
      const map = {};
      (intRes.data.integrations || []).forEach(i => { map[i.provider] = i; });
      setConnected(map);
      const oMap = {};
      (oauthRes.data.providers || []).forEach(p => { oMap[p.provider] = p; });
      setOauthProviders(oMap);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleConnect = async (provider, config) => {
    try {
      const r = await api('post', '/api/platform/integrations/connect', { provider, config });
      if (r.data.success) {
        setConnected(c => ({ ...c, [provider]: r.data.integration }));
        toast.success(`${provider} succesvol gekoppeld!`);
        setConnectingItem(null);
        return true;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Koppeling mislukt. Controleer uw gegevens.';
      toast.error(msg);
      return false;
    }
  };

  const handleOAuthConnect = async (provider) => {
    try {
      const r = await api('get', `/api/integrations/oauth/init/${provider}`);
      if (r.data.auth_url) {
        window.location.href = r.data.auth_url;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'OAuth niet geconfigureerd voor deze provider.';
      toast.error(msg);
    }
  };

  const handleDisconnect = async (provider) => {
    try {
      await api('post', '/api/platform/integrations/disconnect', { provider });
      setConnected(c => { const n = { ...c }; delete n[provider]; return n; });
      toast.success('Ontkoppeld');
    } catch (e) {
      toast.error('Ontkoppelen mislukt');
    }
  };

  const [testingProvider, setTestingProvider] = useState(null);

  const handleTest = async (provider) => {
    setTestingProvider(provider);
    const toastId = toast.loading(`Testen van ${provider} koppeling…`);
    try {
      const r = await api('post', `/api/platform/integrations/${provider}/test`);
      const d = r.data || {};
      if (d.skipped && d.ok) {
        toast.info(d.reason || 'Geen test mogelijk voor deze provider', { id: toastId, duration: 6000 });
      } else if (d.ok) {
        const details = d.live_data ? ` — ${Object.entries(d.live_data).slice(0, 2).map(([k, v]) => `${k}: ${v}`).join(', ')}` : '';
        toast.success(`✓ Koppeling werkt${details}`, { id: toastId, duration: 6000 });
      } else {
        toast.error(`✗ ${d.error || 'Test mislukt'}`, { id: toastId, duration: 8000 });
      }
    } catch (e) {
      toast.error('Test kon niet worden uitgevoerd', { id: toastId });
    }
    setTestingProvider(null);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: '32px', height: '32px', border: '3px solid #f1f5f9', borderTopColor: '#D4A017', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  const totalConnected = Object.keys(connected).length;
  const totalAvailable = INTEGRATIONS.reduce((a, c) => a + c.items.length, 0);
  const pctConnected = totalAvailable > 0 ? Math.round((totalConnected / totalAvailable) * 100) : 0;

  const CATEGORY_META = {
    'Google': {
      icon: '🔵', tagline: 'Voor SEO, lokale vindbaarheid en advertenties via Google',
      benefit: 'Live data uit Google Analytics, Search Console, Maps en Ads — direct in uw dashboard.',
    },
    'Social Media': {
      icon: '📱', tagline: 'Voor advertenties en sociale aanwezigheid',
      benefit: 'Beheer Meta, Instagram en TikTok campagnes vanuit één plek + krijg AI-tips per platform.',
    },
    'Website Builders': {
      icon: '🌐', tagline: 'Voor automatische website verbeteringen',
      benefit: 'Onze AI past meta-tags, headings en SEO-optimalisaties direct toe op uw site.',
    },
    'Overig': {
      icon: '🔧', tagline: 'Email marketing en analytics tools',
      benefit: 'Combineer extra tools voor een 360° marketing-overzicht.',
    },
  };

  return (
    <div data-testid="integratie-page">
      {/* ── Header ── */}
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '22px', fontWeight: 800, color: '#0f172a', marginBottom: '4px' }}>
          Integraties
        </h1>
        <p style={{ color: '#64748b', fontSize: '14px' }}>
          Koppel uw accounts voor live data, AI-tips en automatische optimalisatie. Uw accounts blijven uw eigendom.
        </p>
      </div>

      {/* ── Progress hero ── */}
      <div data-testid="integration-progress" style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 100%)',
        borderRadius: '14px', padding: '22px 26px', marginBottom: '20px', color: 'white',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '14px', marginBottom: '14px' }}>
          <div>
            <p style={{ fontSize: '11px', color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: '4px' }}>
              Voortgang
            </p>
            <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800 }}>
              {totalConnected} van {totalAvailable} accounts gekoppeld
            </p>
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.55)', marginTop: '2px' }}>
              {totalConnected === 0 ? 'Koppel uw eerste account om live data te zien.'
                : totalConnected < 3 ? 'Goed begin! Koppel er meer voor compleet inzicht.'
                : 'Geweldig! U haalt het maximum uit uw dashboard.'}
            </p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '40px', fontWeight: 800, color: '#D4A017', lineHeight: 1 }}>
              {pctConnected}<span style={{ fontSize: '18px', color: 'rgba(255,255,255,0.6)' }}>%</span>
            </p>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginTop: '2px' }}>
              Compleetheid
            </p>
          </div>
        </div>
        {/* Progress bar */}
        <div style={{ height: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '6px', overflow: 'hidden' }}>
          <div style={{
            width: `${pctConnected}%`, height: '100%',
            background: 'linear-gradient(90deg, #D4A017, #f5c842)',
            borderRadius: '6px', transition: 'width 0.4s',
          }} />
        </div>
      </div>

      {/* ── Categories ── */}
      {INTEGRATIONS.map(cat => {
        const meta = CATEGORY_META[cat.category] || {};
        const catConnectedCount = cat.items.filter(i => connected[i.id]).length;
        return (
          <div key={cat.category} data-testid={`integration-category-${cat.category.toLowerCase().replace(/\s/g, '-')}`} style={{ marginBottom: '24px' }}>
            {/* Category header */}
            <div style={{
              background: 'white', borderRadius: '14px 14px 0 0', border: '1px solid #f1f5f9',
              borderBottom: 'none', padding: '16px 20px', display: 'flex',
              alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '22px' }}>{meta.icon}</span>
                <div>
                  <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '15px', fontWeight: 800, color: '#0f172a' }}>
                    {cat.category}
                  </h2>
                  <p style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>{meta.tagline}</p>
                </div>
              </div>
              <span style={{
                fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '6px',
                background: catConnectedCount === cat.items.length ? '#dcfce7' : catConnectedCount > 0 ? '#fef3c7' : '#f1f5f9',
                color: catConnectedCount === cat.items.length ? '#16a34a' : catConnectedCount > 0 ? '#a16207' : '#64748b',
              }}>
                {catConnectedCount}/{cat.items.length} gekoppeld
              </span>
            </div>

            {/* Items grid */}
            <div style={{
              background: '#f8fafc', borderRadius: '0 0 14px 14px', border: '1px solid #f1f5f9',
              borderTop: 'none', padding: '14px',
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '10px' }}>
                {cat.items.map(item => {
                  const isConnected = !!connected[item.id];
                  const oauthAvailable = oauthProviders[item.id]?.configured;
                  return (
                    <div key={item.id} data-testid={`integration-${item.id}`} style={{
                      background: 'white', borderRadius: '12px',
                      border: `1px solid ${isConnected ? '#86efac' : '#e2e8f0'}`,
                      padding: '14px', transition: 'all 0.15s',
                      boxShadow: isConnected ? '0 1px 0 #bbf7d0' : 'none',
                    }}>
                      {/* Icon + name + status */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '9px', flexShrink: 0,
                          background: `${item.color}12`, display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: '18px',
                        }}>
                          {item.icon}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{item.name}</p>
                          {isConnected ? (
                            <p style={{ fontSize: '10px', color: '#16a34a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '3px', marginTop: '1px' }}>
                              <Check size={10} /> Actief
                              {connected[item.id]?.auth_type === 'oauth' && (
                                <span style={{ fontSize: '9px', fontWeight: 700, color: '#9333ea', background: '#f3e8ff', padding: '1px 5px', borderRadius: '4px', marginLeft: '4px' }}>OAuth</span>
                              )}
                            </p>
                          ) : oauthAvailable ? (
                            <p style={{ fontSize: '10px', color: '#9333ea', fontWeight: 600, marginTop: '1px' }}>
                              ⚡ 1-klik OAuth beschikbaar
                            </p>
                          ) : (
                            <p style={{ fontSize: '10px', color: '#94a3b8', marginTop: '1px' }}>Niet gekoppeld</p>
                          )}
                        </div>
                      </div>

                      {/* Description */}
                      <p style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.5, marginBottom: '12px' }}>
                        {item.desc}
                      </p>

                      {/* Action button */}
                      {isConnected ? (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            data-testid={`test-${item.id}`}
                            onClick={() => handleTest(item.id)}
                            disabled={testingProvider === item.id}
                            style={{
                              flex: 1, background: '#0f172a', color: 'white', border: 'none',
                              borderRadius: '8px', padding: '8px 12px', fontSize: '11px',
                              fontWeight: 700, cursor: testingProvider === item.id ? 'wait' : 'pointer',
                              opacity: testingProvider === item.id ? 0.6 : 1,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                            }}
                          >
                            {testingProvider === item.id ? (
                              <><RefreshCw size={12} style={{ animation: 'spin 1s linear infinite' }} /> Testen…</>
                            ) : (
                              <><Zap size={11} /> Test</>
                            )}
                          </button>
                          <Link
                            data-testid={`data-${item.id}`}
                            to="/platform"
                            style={{
                              background: '#f1f5f9', border: 'none', borderRadius: '8px',
                              padding: '8px 12px', fontSize: '11px', fontWeight: 600, color: '#374151',
                              textAlign: 'center', textDecoration: 'none',
                            }}>
                            Data
                          </Link>
                          <button
                            data-testid={`disconnect-${item.id}`}
                            onClick={() => handleDisconnect(item.id)}
                            style={{
                              background: 'white', border: '1px solid #fecaca', borderRadius: '8px',
                              padding: '8px 10px', cursor: 'pointer', fontSize: '11px',
                              fontWeight: 600, color: '#dc2626',
                            }}
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ) : oauthAvailable ? (
                        <button
                          data-testid={`oauth-connect-${item.id}`}
                          onClick={() => handleOAuthConnect(item.id)}
                          style={{
                            width: '100%', background: '#0f172a', border: 'none', borderRadius: '8px',
                            padding: '9px 12px', cursor: 'pointer', fontSize: '12px',
                            fontWeight: 700, color: 'white', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', gap: '5px',
                          }}
                        >
                          <Shield size={12} /> Verbinden met OAuth →
                        </button>
                      ) : (
                        <button
                          data-testid={`connect-${item.id}`}
                          onClick={() => setConnectingItem(item)}
                          style={{
                            width: '100%', background: '#D4A017', border: 'none', borderRadius: '8px',
                            padding: '9px 12px', cursor: 'pointer', fontSize: '12px',
                            fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center',
                            justifyContent: 'center', gap: '5px',
                          }}
                        >
                          <Link2 size={12} /> Koppelen →
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Category benefit */}
              {meta.benefit && (
                <div style={{ marginTop: '12px', padding: '10px 14px', background: 'rgba(212,160,23,0.06)', borderRadius: '10px', border: '1px solid rgba(212,160,23,0.15)' }}>
                  <p style={{ fontSize: '11px', color: '#64748b', lineHeight: 1.5 }}>
                    <strong style={{ color: '#D4A017' }}>💡 Voordeel:</strong> {meta.benefit}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* ── Security info ── */}
      <div style={{
        background: 'white', borderRadius: '12px', padding: '16px 20px',
        border: '1px solid #f1f5f9', marginTop: '8px',
        display: 'flex', gap: '12px', alignItems: 'flex-start',
      }}>
        <Shield size={18} style={{ color: '#16a34a', flexShrink: 0, marginTop: '2px' }} />
        <div>
          <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>
            Veilig & u behoudt eigendom
          </p>
          <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.6 }}>
            Tokens en API keys worden versleuteld opgeslagen. U kunt elke koppeling met één klik verwijderen.
            Uw advertentieaccounts en data blijven uw eigendom — wij krijgen alleen leesrechten om voor u te kunnen analyseren en optimaliseren.
          </p>
        </div>
      </div>

      {connectingItem && (
        <ConnectModal
          integration={connectingItem}
          onClose={() => setConnectingItem(null)}
          onConnect={handleConnect}
        />
      )}
    </div>
  );
}
